import type { SortOption } from "@/constants/filterOptions";
import { GENRE_OPTIONS } from "@/constants/genreOptions";
import type {
  ExperimentalRecommenderQueryParams,
  RecommenderQueryParams,
  ScoredRecommenderQueryParams
} from "@/generatedApi/spotSearchEngineApi";
import { MoodCategory } from "@/generatedApi/spotSearchEngineApi";
import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { locationAtom } from "./recommendationAtom";
import { userAtom } from "./userAtom";

// 基本フィルタ設定のタイプ定義
export type RecommendationFilterSettings = {
  address_pref: string[];
  mood_category: string | null;
  genre: string | null;
};

// デフォルト値
const defaultFilterSettings: RecommendationFilterSettings = {
  address_pref: [],
  mood_category: null,
  genre: null
};

// 定数を別ファイルからインポート
export { ALL_PREFECTURE_VALUES, PREFECTURE_OPTIONS, SORT_OPTIONS } from "@/constants/filterOptions";
export type { SortOption } from "@/constants/filterOptions";

export { MOOD_CATEGORY_OPTIONS } from "@/constants/moodCategories";

export { GENRE_OPTIONS, getAllGenres } from "@/constants/genreOptions";

// ジャンルのlabelからqueryValueを取得するヘルパー関数
const getGenreQueryValue = (genreLabel: string | null): string | null => {
  if (!genreLabel) return null;

  for (const category of GENRE_OPTIONS) {
    const genre = category.genres.find((g) => g.label === genreLabel);
    if (genre) {
      return genre.queryValue || genre.label;
    }
  }
  return genreLabel; // フォールバック
};

// ローカルストレージに保存されるフィルタ設定
export const recommendationFilterSettingsAtom = atomWithStorage<RecommendationFilterSettings>(
  "recommendation-filter-settings",
  defaultFilterSettings
);

// ソート設定（ローカルストレージに保存、デフォルトは「人気」）
export const sortOptionAtom = atomWithStorage<SortOption>(
  "recommendation-sort-option",
  "popularity"
);

// 最終的なRecommenderQueryParamsを生成するAtom
export const recommendationQueryParamsAtom = atom<RecommenderQueryParams>((get) => {
  const filterSettings = get(recommendationFilterSettingsAtom);
  const user = get(userAtom);
  const location = get(locationAtom);

  // 基本パラメータ構築
  const params: RecommenderQueryParams = {
    size: 10,
    personalized: true
  };

  // ユーザーIDを設定（認証されている場合のみ）
  if (user?.userId) {
    params.user_id = user.userId;
    params.skip_saved_spots = true;
  }

  // 位置情報を設定
  if (location.latitude && location.longitude) {
    params.location = {
      lat: location.latitude,
      lon: location.longitude
    };
    params.distance = 50000; // 50km圏内
  }

  // 都道府県フィルタ
  if (filterSettings.address_pref.length > 0) {
    params.address_pref = filterSettings.address_pref;
  }

  // Mood Categoryフィルタ
  if (filterSettings.mood_category) {
    params.mood_category = [filterSettings.mood_category as MoodCategory];
  }

  // ジャンルフィルタ（labelをqueryValueに変換）
  if (filterSettings.genre) {
    const genreQueryValue = getGenreQueryValue(filterSettings.genre);
    if (genreQueryValue) {
      params.genre = genreQueryValue;
    }
  }

  return params;
});

// Experimental recommendation query params with score function
export const experimentalRecommendationQueryParamsAtom = atom<ExperimentalRecommenderQueryParams>(
  (get) => {
    const filterSettings = get(recommendationFilterSettingsAtom);
    const user = get(userAtom);
    const location = get(locationAtom);
    const sortOption = get(sortOptionAtom);

    // 位置情報の有無を判定
    const hasLocation = !!(location.latitude && location.longitude);

    // 基本パラメータ構築（フォールバック用の簡易実装）
    const defaultScoreFunction = hasLocation
      ? sortOption === "distance"
        ? "1000 * (1 / geoDistance) + userInterestScore * 100 + spotSimilarityScore * 100 + googleRatingScore * 0.1 + moodCategoryScore * 100 + genreScore * 100"
        : "100 * (1 / geoDistance) + userInterestScore * 100 + spotSimilarityScore * 100 + googleRatingScore * 0.1 + moodCategoryScore * 1000 + genreScore * 1000"
      : sortOption === "distance"
      ? "userInterestScore * 100 + spotSimilarityScore * 100 + googleRatingScore * 0.1 + moodCategoryScore * 100 + genreScore * 100"
      : "userInterestScore * 100 + spotSimilarityScore * 100 + googleRatingScore * 0.1 + moodCategoryScore * 1000 + genreScore * 1000";

    const params: ExperimentalRecommenderQueryParams = {
      size: 10,
      personalized: true,
      score_function: defaultScoreFunction
    };

    // ユーザーIDを設定（認証されている場合のみ）
    if (user?.userId) {
      params.user_id = user.userId;
      params.skip_saved_spots = true;
    }

    // 位置情報を設定
    if (location.latitude && location.longitude) {
      params.location = {
        lat: location.latitude,
        lon: location.longitude
      };
      params.distance = 50000; // 50km圏内
    }

    // 都道府県フィルタ
    if (filterSettings.address_pref.length > 0) {
      params.address_pref = filterSettings.address_pref;
    }

    // Mood Categoryフィルタ
    if (filterSettings.mood_category) {
      params.mood_category = [filterSettings.mood_category as MoodCategory];
    }

    // ジャンルフィルタ
    if (filterSettings.genre) {
      params.genre = filterSettings.genre;
    }

    return params;
  }
);

// Scored recommendation query params with function type
export const scoredRecommendationQueryParamsAtom = atom<ScoredRecommenderQueryParams>((get) => {
  const filterSettings = get(recommendationFilterSettingsAtom);
  const user = get(userAtom);
  const location = get(locationAtom);
  const sortOption = get(sortOptionAtom);

  // 基本パラメータ構築
  const params: ScoredRecommenderQueryParams = {
    size: 10,
    personalized: true,
    function_type: sortOption // distance or popularity
  };

  // ユーザーIDを設定（認証されている場合のみ）
  if (user?.userId) {
    params.user_id = user.userId;
    params.skip_saved_spots = true;
  }

  // 位置情報を設定
  if (location.latitude && location.longitude) {
    params.location = {
      lat: location.latitude,
      lon: location.longitude
    };
    params.distance = 50000; // 50km圏内
  }

  // 都道府県フィルタ
  if (filterSettings.address_pref.length > 0) {
    params.address_pref = filterSettings.address_pref;
  }

  // Mood Categoryフィルタ
  if (filterSettings.mood_category) {
    params.mood_category = [filterSettings.mood_category as MoodCategory];
  }

  // ジャンルフィルタ（labelをqueryValueに変換）
  if (filterSettings.genre) {
    const genreQueryValue = getGenreQueryValue(filterSettings.genre);
    if (genreQueryValue) {
      params.genre = genreQueryValue;
    }
  }

  return params;
});

// フィルタが設定されているかどうかを判定するAtom
export const hasActiveFiltersAtom = atom<boolean>((get) => {
  const filterSettings = get(recommendationFilterSettingsAtom);
  return (
    filterSettings.address_pref.length > 0 ||
    filterSettings.mood_category !== null ||
    filterSettings.genre !== null
  );
});
